/**
 * AppShell Component
 * 메인 레이아웃 (헤더 + 사이드바 + 컨텐츠)
 */

import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, isDemo } from '../../firebase/config';
import { useAuthorization } from '../hooks/useAuthorization';
import { Sidebar } from './Sidebar';
import { LogOut } from 'lucide-react';
import { GlobalPopupManager } from './GlobalPopupManager';
import { clearPopupSessionState } from '../hooks/usePopupAnnouncements';

/**
 * 애플리케이션 메인 레이아웃
 */
export function AppShell() {
  const { user } = useAuthorization();
  const navigate = useNavigate();
  const location = useLocation();
  const popupUid = user?.profile?.id || null;

  // 로그아웃 처리
  const handleLogout = async () => {
    try {
      // 데모 모드: 로그아웃 불가 안내
      if (isDemo || !auth) {
        alert('🎭 데모 모드에서는 로그아웃할 수 없습니다.\n페이지를 새로고침하면 다시 자동 로그인됩니다.');
        return;
      }

      // 실제 Firebase 로그아웃
      console.log('[AppShell redirect /login]', { reason: 'manual signOut' });
      clearPopupSessionState(); // V70G7B
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  // 현재 페이지 제목 가져오기
  const getPageTitle = (): string => {
    const path = location.pathname;

    // 동적 경로 처리 (직원 상세 페이지 등)
    if (path.startsWith('/hr/employees/') && path !== '/hr/employees') {
      return '인사관리 > 직원상세';
    }

    // 경로에 따른 페이지 제목 매핑
    const pageTitles: Record<string, string> = {
      '/dashboard': '대시보드',
      '/notice': '공지사항',
      '/work/my-dashboard': '근로관리 > 개인 대시보드',
      '/work/schedule': '근로관리 > 근로일정',
      '/work/my-approvals': '근로관리 > 내 승인요청',
      '/work/approvals': '근로관리 > 승인관리',
      '/hr/employees': '인사관리 > 직원관리',
      '/hr/employment-status': '인사관리 > 재직현황',
      '/hr/org': '인사관리 > 조직관리',
      '/hr/leave-settings': '인사관리 > 휴가설정',
      '/hr/holidays': '인사관리 > 공휴일관리',
      '/hr/performance': '인사관리 > 인사평가현황',
      '/team/management': '팀관리 > 팀관리',
      '/team/leaders': '팀관리 > 조직장관리',
      '/master/positions': '마스터데이터 > 직급',
      '/master/titles': '마스터데이터 > 직책',
      '/master/employment-types': '마스터데이터 > 고용형태',
      '/master/work-types': '마스터데이터 > 근로타입',
      '/master/work-schedule': '마스터데이터 > 근로일정',
      '/master/leave-types': '마스터데이터 > 휴가타입',
      '/master/leave-policies': '마스터데이터 > 휴가정책',
      '/master/appointment-types': '마스터데이터 > 발령타입',
      '/master/project-status': '마스터데이터 > 프로젝트상태',
      '/admin/roles': 'Admin > 역할 관리',
      '/admin/test-utilities': 'Admin > 테스트도구', // V47.15
    };

    return pageTitles[path] || '대시보드';
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f9fafb' }}>
      {/* 사이드바 */}
      <Sidebar />

      {/* 메인 콘텐츠 — flex column으로 헤더/본문 높이 분배 */}
      {/* V70 2026-10-19 — 마블 결과 팝업 정확한 중앙 정렬 위한 명시 식별자 (DOM 추정 방식 교체) */}
      <div data-app-shell-main="1" style={{
        marginLeft: '280px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as const,
        height: '100vh',
      }}>
        {/* 헤더 */}
        <header style={{
          background: '#f5f5f5',
          borderBottom: '1px solid #e5e5e5',
          padding: '8px 32px',
          position: 'sticky',
          top: 0,
          zIndex: 90
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{
              fontSize: '14px',
              color: 'var(--color-gray-600)'
            }}>
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long'
              })}
            </div>

            {/* 로그아웃 버튼 */}
            <button
                onClick={handleLogout}
                style={{
                  padding: '4px 12px',
                  background: 'white',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#ef4444',
                  fontWeight: '500',
                  fontSize: '13px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fee2e2';
                  e.currentTarget.style.borderColor = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = 'var(--color-gray-300)';
                }}
            >
              <LogOut size={16} />
              <span>로그아웃</span>
            </button>
          </div>
        </header>

        {/* 페이지 콘텐츠 — flex: 1로 헤더 제외 남은 높이 전부 사용 */}
        <main style={{ padding: '32px', flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Outlet />
        </main>
      </div>

      {/* V70G7B: 팝업 공지 — uid 변경 시 재마운트로 이전 사용자 상태 초기화 */}
      {!isDemo && popupUid && <GlobalPopupManager key={popupUid} uid={popupUid} />}
    </div>
  );
}
